import { Component, OnInit, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { StorageService } from 'src/app/core/services/storage/storage.service';
import { CookieSettingsService } from '../../services/cookie-settings.service';
import { Subscription } from 'rxjs';
import { PayPalService } from '../paypal-payment/paypal-payment.service';

@Component({
  selector: 'app-cookie-consent',
  templateUrl: './cookie-consent.component.html',
  styleUrls: ['./cookie-consent.component.css']
})
export class CookieConsentComponent implements OnInit, OnDestroy {
  cookieAccepted = true;
  currentLanguage = 'EN';
  showCustomize = false;
  activeTab: 'overview' | 'required' | 'analytics' | 'marketing' = 'overview';

  // Individual consent preferences
  requiredCookies = true; // Always true, cannot be disabled
  analyticalCookies = false;
  marketingCookies = false;

  private settingsSubscription: Subscription;

  constructor(
    private translate: TranslateService,
    private storageService: StorageService,
    private cookieSettingsService: CookieSettingsService,
    private payPalService: PayPalService,
  ) {
    this.currentLanguage = this.translate.currentLang;
    this.translate.onLangChange.subscribe((language) => {
      this.currentLanguage = language.lang;
    });
  }

  ngOnInit(): void {
    const cookieInfo = this.storageService.getCookie('tresInfo');
    if (cookieInfo.length) {
      this.cookieAccepted = true;
      // Load existing preferences and activate tracking scripts
      try {
        const preferences = JSON.parse(cookieInfo);
        this.analyticalCookies = preferences.analyticalCookies || false;
        this.marketingCookies = preferences.marketingCookies || false;

        // Load tracking scripts based on saved consent (after page reload)
        this.loadTrackingScripts(preferences);
      } catch (e) {
        // If parsing fails, use defaults
      }
    } else {
      this.cookieAccepted = false;
    }

    // Listen for settings reopening from floating button
    this.settingsSubscription = this.cookieSettingsService.showSettings$.subscribe(show => {
      if (show) {
        this.cookieAccepted = false; // Reopen the modal
        this.showCustomize = false;  // Reset to main view
      }
    });
  }

  ngOnDestroy(): void {
    if (this.settingsSubscription) {
      this.settingsSubscription.unsubscribe();
    }
  }

  acceptAllCookies(): void {
    const data = {
      requiredCookies: true,
      analyticalCookies: true,
      marketingCookies: true,
      consentTimestamp: new Date().toISOString(),
      consentVersion: '1.0'
    };
    this.storageService.setCookie('tresInfo', data);
    this.cookieAccepted = true;
    this.cookieSettingsService.closeSettings();
    this.initializeGtagForAds();
    // Reload page to activate cookies with new consent settings
    window.location.reload();
  }

  rejectAllCookies(): void {
    // Clear all tracking cookies first
    this.clearAllTrackingCookies();

    // Only set required cookies, reject all optional ones
    const data = {
      requiredCookies: true,
      analyticalCookies: false,
      marketingCookies: false,
      consentTimestamp: new Date().toISOString(),
      consentVersion: '1.0'
    };
    this.storageService.setCookie('tresInfo', data);
    this.cookieAccepted = true;
    this.cookieSettingsService.closeSettings();

    // Reload page to clear third-party cookies and stop any loaded tracking scripts
    window.location.reload();
  }

  private clearAllTrackingCookies(): void {
    // Get all cookies
    const cookies = document.cookie.split(';');

    // Delete ALL cookies - no exceptions
    cookies.forEach(cookie => {
      const cookieName = cookie.split('=')[0].trim();
      if (cookieName) {
        // Try multiple path and domain combinations to ensure deletion
        const paths = ['/', ''];
        const domains = [
          '',
          window.location.hostname,
          '.' + window.location.hostname,
          '.localhost',
          '.tresorvino.de'
        ];

        paths.forEach(path => {
          domains.forEach(domain => {
            // Without domain
            document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path || '/'};`;
            // With domain
            if (domain) {
              document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path || '/'}; domain=${domain};`;
            }
          });
        });
      }
    });

    // Clear ALL localStorage
    // localStorage.clear();

    // Clear ALL sessionStorage
    // sessionStorage.clear();
  }

  private clearAnalyticsCookies(): void {
    // Google Analytics cookies
    const analyticsCookies = [
      '_ga',
      '_gid',
      '_gat',
      '_gac_',
      '__utma',
      '__utmb',
      '__utmc',
      '__utmz',
      '__utmv'
    ];

    // Microsoft Clarity cookies
    const clarityCookies = [
      '_clck',
      '_clsk',
      'CLID',
      'MUID',
      'ANONCHK',
      'MR',
      'SM'
    ];

    const allAnalyticsCookies = [...analyticsCookies, ...clarityCookies];
    this.deleteCookiesByNames(allAnalyticsCookies);

    // Also delete cookies that start with _ga_ (container specific)
    this.deleteCookiesStartingWith('_ga_');
  }

  private clearMarketingCookies(): void {
    // Google Ads/AdSense cookies
    const marketingCookies = [
      '_gcl_au',
      '_gcl_aw',
      '_gcl_dc',
      '_gac_',
      'IDE',
      'DSID',
      'FLC',
      'AID',
      'TAID',
      '__gads',
      '__gpi',
      '__gpi_optout',
      'test_cookie',
      'FCCDCF',
      'FCNEC'
    ];

    this.deleteCookiesByNames(marketingCookies);

    // Delete cookies that start with _gac_ (property specific)
    this.deleteCookiesStartingWith('_gac_');
  }

  private deleteCookiesByNames(cookieNames: string[]): void {
    const paths = ['/', ''];
    const domains = [
      '',
      window.location.hostname,
      '.' + window.location.hostname,
      '.tresorvino.de'
    ];

    cookieNames.forEach(cookieName => {
      paths.forEach(path => {
        domains.forEach(domain => {
          // Without domain
          document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path || '/'};`;
          // With domain
          if (domain) {
            document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path || '/'}; domain=${domain};`;
          }
        });
      });
    });
  }

  private deleteCookiesStartingWith(prefix: string): void {
    const cookies = document.cookie.split(';');
    const paths = ['/', ''];
    const domains = [
      '',
      window.location.hostname,
      '.' + window.location.hostname,
      '.tresorvino.de'
    ];

    cookies.forEach(cookie => {
      const cookieName = cookie.split('=')[0].trim();
      if (cookieName.startsWith(prefix)) {
        paths.forEach(path => {
          domains.forEach(domain => {
            document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path || '/'};`;
            if (domain) {
              document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path || '/'}; domain=${domain};`;
            }
          });
        });
      }
    });
  }

  showCustomizeOptions(): void {
    this.showCustomize = true;
    this.activeTab = 'overview'; // Reset to overview tab
  }

  saveCustomPreferences(): void {
    // Remove cookies for disabled categories before saving
    if (!this.analyticalCookies) {
      this.clearAnalyticsCookies();
    }
    if (!this.marketingCookies) {
      this.clearMarketingCookies();
    }

    const data = {
      requiredCookies: true, // Always required
      analyticalCookies: this.analyticalCookies,
      marketingCookies: this.marketingCookies,
      consentTimestamp: new Date().toISOString(),
      consentVersion: '1.0'
    };
    this.storageService.setCookie('tresInfo', data);
    this.cookieAccepted = true;
    this.cookieSettingsService.closeSettings();

    // Reload page to activate cookies with new consent settings
    window.location.reload();
  }

  private loadTrackingScripts(consent: any): void {
    // Load paypal cookies by default
    this.payPalService.loadPayPalScript();
    
    // Load Google Analytics only if analytical cookies are accepted
    if (consent.analyticalCookies) {
      this.loadGoogleAnalytics();
      this.loadGoogleTagManager();
      this.loadMicrosoftClarity();
    }

    // Load marketing scripts only if marketing cookies are accepted
    if (consent.marketingCookies) {
      this.loadGoogleAdsense();
    }
  }

  private loadGoogleAnalytics(): void {
    // Check if script is already loaded
    if (!document.querySelector('script[src*="googletagmanager.com/gtag/js?id=G-"]')) {
      const script = document.createElement('script');
      script.async = true;
      script.src = 'https://www.googletagmanager.com/gtag/js?id=G-MLWQ10TK07';
      document.head.appendChild(script);

      script.onload = () => {
        this.initializeGoogleAnalytics();
      };
    } else {
      // Script already loaded, just initialize
      this.initializeGoogleAnalytics();
    }
  }

  private initializeGoogleAnalytics(): void {
    // Initialize dataLayer
    (window as any).dataLayer = (window as any).dataLayer || [];

    // Define gtag function properly using arguments object
    if (!(window as any).gtag) {
      (window as any).gtag = function() {
        (window as any).dataLayer.push(arguments);
      };
    }

    const gtag = (window as any).gtag;

    // Initialize Google Analytics
    gtag('js', new Date());
    gtag('config', 'G-MLWQ10TK07', {
      'cookie_domain': window.location.hostname,
      'cookie_flags': 'SameSite=None;Secure'
    });
  }

  private loadGoogleTagManager(): void {
    if (!(window as any).google_tag_manager) {
      const script = document.createElement('script');
      script.innerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
        new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
        j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
        'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
        })(window,document,'script','dataLayer','GTM-NB6S5ZKL');`;
      document.head.appendChild(script);
    }
  }

  private loadMicrosoftClarity(): void {
    if (!(window as any).clarity) {
      const script = document.createElement('script');
      script.innerHTML = `(function(c,l,a,r,i,t,y){c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
        t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
        y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
        })(window,document,"clarity","script","siy7pcy10a");`;
      document.head.appendChild(script);
    }
  }

  private loadGoogleAdsense(): void {
    // Load Google Ads/Adsense script
    if (!document.querySelector('script[src*="adsbygoogle"]')) {
      const script = document.createElement('script');
      script.async = true;
      script.src = 'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-1115035479249914';
      script.crossOrigin = 'anonymous';
      document.head.appendChild(script);
    }

    // Load Google Funding Choices (for personalized ads consent)
    if (!document.querySelector('script[src*="fundingchoicesmessages"]')) {
      const fcScript = document.createElement('script');
      fcScript.async = true;
      fcScript.src = 'https://fundingchoicesmessages.google.com/i/pub-1115035479249914?ers=1';
      document.head.appendChild(fcScript);
    }

    // Configure gtag for ads
    this.initializeGtagForAds();
  }

  initializeGtagForAds(): void {
    // Ensure dataLayer and gtag are available
    (window as any).dataLayer = (window as any).dataLayer || [];
    if (!(window as any).gtag) {
      (window as any).gtag = function() {
        (window as any).dataLayer.push(arguments);
      };
    }

    const gtag = (window as any).gtag;

    // Load gtag.js for Google Ads (this sets _gcl_au cookie)
    if (!document.querySelector('script[src*="googletagmanager.com/gtag/js?id=AW-"]')) {
      const gtagScript = document.createElement('script');
      gtagScript.async = true;
      gtagScript.src = 'https://www.googletagmanager.com/gtag/js?id=AW-11543897386';
      document.head.appendChild(gtagScript);
    }

    // Initialize gtag
    gtag('js', new Date());

    // Configure Google Ads conversion tracking (sets _gcl_au cookie)
    gtag('config', 'AW-11543897386');

    // Configure GA4 with ads features enabled
    gtag('config', 'G-MLWQ10TK07', {
      'allow_enhanced_conversions': true
    });

    // Enable conversion linker for cross-domain tracking
    gtag('set', 'linker', {
      'domains': [window.location.hostname]
    });
  }
}
